import { Result } from "./types";

/** A cache as `clearTogether` sees it. */
export type CacheHandle = { clear: () => void };

/**
 * Share one in-flight promise per key across concurrent callers, with clear-safe helpers.
 *
 * `getCache` must read the live module binding (clear functions replace the Map wholesale),
 * so `isCurrent`/`evict` stay truthful when the cache is cleared mid-fetch: a fetch that
 * outlives a clear must not evict a newer in-flight entry. The body starts on a microtask
 * so the entry is registered before it runs — `isCurrent`/`evict` are safe anywhere inside.
 */
const memoizeInFlight = <T>(
  getCache: () => Map<string, Promise<T>>,
  key: string,
  run: (ctx: { isCurrent: () => boolean; evict: () => void }) => Promise<T>
): Promise<T> => {
  const cache = getCache();
  if (!cache.has(key)) {
    let promise: Promise<T>;
    const isCurrent = () => getCache().get(key) === promise;
    const evict = () => {
      if (isCurrent()) getCache().delete(key);
    };
    promise = Promise.resolve().then(() => run({ isCurrent, evict }));
    // A thrown body (as opposed to a returned Result failure, which bodies evict themselves)
    // must not stay memoized — e.g. a transient Office host error inside Excel.run
    promise.catch(() => evict());
    cache.set(key, promise);
  }
  return getCache().get(key)!;
};

export const clearTogether = (caches: CacheHandle[]): void => {
  caches.forEach((cache) => cache.clear());
};

/**
 * An in-memory cache of one API resource per scope. A failed or empty fetch is never kept, so the
 * next read retries.
 */
export const defineCachedResource = <Scope, T>({
  scopeKey,
  fetch,
}: {
  scopeKey: (scope: Scope) => string;
  fetch: (scope: Scope) => Promise<Result<T>>;
}) => {
  let cache: Map<string, Promise<Result<T>>> = new Map();

  const get = (scope: Scope): Promise<Result<T>> =>
    memoizeInFlight(
      () => cache,
      scopeKey(scope),
      async ({ evict }) => {
        const result = await fetch(scope);
        if (!result.success || !result.data) evict();
        return result;
      }
    );

  const retainOnly = (scope: Scope): void => {
    const keep = scopeKey(scope);
    const others: string[] = [];
    cache.forEach((_, cachedKey) => {
      if (cachedKey !== keep) others.push(cachedKey);
    });
    others.forEach((cachedKey) => cache.delete(cachedKey));
  };

  const watchForClear = (): (() => boolean) => {
    const cacheAtStart = cache;
    return () => cache === cacheAtStart;
  };

  /**
   * Swaps in data the caller already fetched. A refresh fetches every part of its datasource
   * first, then stages them all in one tick, so no reader sees some parts new and others old.
   */
  const stage = (scope: Scope, data: T, stillCurrent: () => boolean = () => true): Result<T> => {
    const result: Result<T> = { success: true, data };
    if (!stillCurrent()) return result;
    cache.set(scopeKey(scope), Promise.resolve(result));
    retainOnly(scope);
    return result;
  };

  /** Fetches fresh and swaps it in on success; a failure leaves the cached copy in place. */
  const refresh = async (scope: Scope, stillCurrent: () => boolean = () => true): Promise<Result<T>> => {
    const notCleared = watchForClear();
    const result = await fetch(scope);
    if (result.success && result.data && notCleared()) stage(scope, result.data, stillCurrent);
    return result;
  };

  const clear = (): void => {
    cache = new Map();
  };

  return { get, refresh, stage, clear, watchForClear };
};

export const defineCachedLookup = <T>(fetch: () => Promise<Result<T>>) => {
  const resource = defineCachedResource<void, T>({ scopeKey: () => "lookup", fetch: () => fetch() });
  return { get: () => resource.get(), clear: resource.clear };
};

export const defineCachedResourceById = <T>(fetch: (id: string) => Promise<Result<T>>) =>
  defineCachedResource<string, T>({ scopeKey: (id) => id, fetch });
